import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
    addToCart,
    addMultipleToCart,
    addCTOToCart,
    launchCartFlyout,
    updateCart,
    fetchCart,
    REFRESH_CART,
} from '../utility/utility-actions';
import { getCTOLink } from '../core/src/etr-api-client/cto';

/**
 *
 * @param {boolean} trackAddToCart option to track add to cart success with addedToCart return prop
 *
 */

/**
 * @typedef {Object} AddToCartFunctions
 * @property {function(): promise} addCTOToCart - Adds a CTO preconfig to cart
 * @property {function(): promise} addToCart - Adds a STO product to cart
 * @property {function(): promise} addMultipleToCart - Add multiple STO products to cart
 * @property {function(): promise} goToCTOPage - Link to CTO page using react router
 * @property {function(): promise} launchCartFlyout - Launch cart flyout with last item added to cart displayed
 * @property {boolean} addedToCart - boolean indicator if item has been added to cart, has 2 sec reset
 */

/**
 * Returns redux connected add to cart functions
 * @name useAddToCart
 * @returns {AddToCartFunctions}
 */
export default function useAddToCart(trackAddToCart, options = {}) {
    const dispatch = useDispatch();
    const history = useHistory();
    const ctoConfigurations = useSelector(state => {
        const { withCTOConfigurations } = options;
        if (withCTOConfigurations) {
            return state.productData.ctoConfigurations;
        }
        return null;
    });

    const [addedToCart, setAddedToCart] = useState({});

    useEffect(() => {
        let timer;
        if (Object.keys(addedToCart).length > 0) {
            timer = setTimeout(() => {
                setAddedToCart({});
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [addedToCart]);

    return {
        addCTOToCart: (product, quantity, blockFlyout, followRedirect) =>
            dispatch(addCTOToCart(product, quantity, blockFlyout, followRedirect)),
        addToCart: (product, quantity, blockFlyout, addOns, variant, baseProductSku, xsellType, metricParams) => {
            trackAddToCart && setAddedToCart({ [product.sku]: true });
            return dispatch(
                addToCart(product, quantity, blockFlyout, addOns, variant, baseProductSku, xsellType, metricParams)
            );
        },
        addMultipleToCart: (products, quantity, blockFlyout, options, baseProductSku, xsellType) =>
            dispatch(addMultipleToCart(products, quantity, blockFlyout, options, baseProductSku, xsellType)),
        goToCTOPage: (product, quantity, additionalParams = {}) => {
            const { sku } = product;
            const ctoParams = (ctoConfigurations && ctoConfigurations[sku]) || {};
            const catEntryId = product.itemId || product.catentryId || product.catEntryId;
            history.push(getCTOLink(catEntryId, quantity, { ...additionalParams, ...ctoParams }));
        },
        getCTOLink: (product, quantity, additionalParams = {}) => {
            const { sku } = product;
            const ctoParams = (ctoConfigurations && ctoConfigurations[sku]) || {};
            const catEntryId = product.itemId || product.catentryId || product.catEntryId;
            return getCTOLink(catEntryId, quantity, { ...additionalParams, ...ctoParams });
        },
        goToGiftCardPage: pdpLink => history.push(pdpLink),
        updateCart: (cartItem, quantity) => dispatch(updateCart(cartItem, quantity)),
        launchCartFlyout: () => dispatch(launchCartFlyout()),
        fetchCart: () => dispatch(fetchCart(REFRESH_CART)),
        addedToCart,
    };
}
